import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, CalendarRange, Users, CheckCircle2, XCircle, CalendarOff } from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/utils-app";
import { StatTile, StatStrip } from "@/components/StatTile";
import { ImageLightbox } from "@/components/ImageLightbox";
import { toast } from "sonner";

function istMonth() {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date()).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}`;
}

function codeStyle(code) {
  switch (code) {
    case "P":
      return "bg-emerald-500 text-white";
    case "A":
      return "bg-rose-200 text-rose-900";
    case "H":
      return "bg-violet-500 text-white";
    default:
      return "bg-secondary text-muted-foreground";
  }
}

function SupervisorCellPopover({ row, cell, onChoose }) {
  const isPast = (() => {
    const today = new Date();
    const tz = today.getTimezoneOffset() * 60000;
    return cell.date <= new Date(today.getTime() - tz).toISOString().slice(0, 10);
  })();
  const Cell = (
    <button
      type="button"
      className={`mx-auto relative flex h-7 w-7 items-center justify-center rounded text-[11px] font-semibold ${codeStyle(cell.code)} hover:opacity-80`}
      title={[
        `${fmtDate(cell.date)} · ${cell.code || "—"}`,
        cell.override ? "Manual override" : null,
      ].filter(Boolean).join(" · ")}
      data-testid={`sup-cal-cell-${row.supervisor_id}-${cell.date}`}
    >
      {cell.code || "·"}
      {cell.override && (
        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500 ring-1 ring-white" />
      )}
    </button>
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{Cell}</PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="center" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 text-xs">
          <div className="font-medium">{fmtDate(cell.date)} · {row.supervisor_name}</div>
          <div className="text-muted-foreground">
            System: <b>{cell.code || "—"}</b>
            {cell.override ? <> · <span className="text-amber-600">overridden</span></> : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <Button size="sm" variant={cell.code === "P" ? "default" : "outline"} onClick={() => onChoose("P")} data-testid={`sup-cal-set-P-${row.supervisor_id}-${cell.date}`}>
            Present
          </Button>
          <Button size="sm" variant={cell.code === "A" ? "default" : "outline"} onClick={() => onChoose("A")} data-testid={`sup-cal-set-A-${row.supervisor_id}-${cell.date}`}>
            Absent
          </Button>
        </div>
        {cell.override && (
          <Button size="sm" variant="ghost" className="mt-2 w-full text-xs" onClick={() => onChoose(null)} data-testid={`sup-cal-set-auto-${row.supervisor_id}-${cell.date}`}>
            Clear override (auto)
          </Button>
        )}
        {!isPast && (
          <div className="mt-1 text-[10px] text-muted-foreground">Tip: overrides apply to past or current dates.</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function SupervisorAttendancePage() {
  const [month, setMonth] = useState(istMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drill, setDrill] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const load = () => {
    if (!month) return;
    setBusy(true);
    api
      .get("/admin/supervisor-attendance/pmc-calendar", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [month]);

  const openDrill = async (row) => {
    try {
      const { data } = await api.get("/admin/supervisor-attendance/worker", {
        params: { supervisor_id: row.supervisor_id, end_date: `${month}-28`, days: 31 },
      });
      setDrill(data);
    } catch {
      // ignore
    }
  };

  const setOverride = async (supervisor_id, date, code) => {
    try {
      await api.post("/admin/supervisor-attendance/override", {
        supervisor_id,
        date,
        code,
      });
      toast.success(code === null ? "Override cleared" : `Marked ${code === "P" ? "Present" : "Absent"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const dayHeaders = useMemo(() => (data?.days || []).map((d) => d.split("-")[2]), [data]);

  // Aggregate totals across the entire month
  const totals = (data?.rows || []).reduce(
    (acc, r) => {
      acc.sups += 1;
      acc.present += r.present_actual || 0;
      acc.absent += r.absent || 0;
      acc.holidays += r.holidays || 0;
      return acc;
    },
    { sups: 0, present: 0, absent: 0, holidays: 0 },
  );

  return (
    <AppShell title="Supervisor Self-Attendance" subtitle="ITCOT supervisor selfie attendance · click any cell to override">
      <div className="flex flex-col gap-4">
        <Card>
          <CardContent className="grid gap-3 p-4 md:grid-cols-12">
            <div className="md:col-span-4 flex flex-col gap-1.5">
              <Label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <CalendarRange className="mr-1 inline h-3 w-3" /> Month
              </Label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="sup-cal-month-input" className="h-10" />
            </div>
            <div className="md:col-span-8 flex items-end gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              A supervisor is Present on any day they captured at least one selfie. Click a cell to manually override.
            </div>
          </CardContent>
        </Card>

        <StatStrip cols={4}>
          <StatTile icon={Users} accent="slate" label="Supervisors" value={totals.sups} testid="sup-cal-stat-sups" />
          <StatTile icon={CheckCircle2} accent="emerald" label="Present (P)" value={totals.present} testid="sup-cal-stat-present" />
          <StatTile icon={XCircle} accent="rose" label="Absent (A)" value={totals.absent} testid="sup-cal-stat-absent" />
          <StatTile icon={CalendarOff} accent="violet" label="Holidays (H)" value={totals.holidays} testid="sup-cal-stat-holidays" />
        </StatStrip>

        {busy ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Loading…</CardContent></Card>
        ) : !data ? null : data.rows.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">No PMC supervisors configured.</CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="sticky top-0 z-10 bg-card">
                    <th className="sticky left-0 z-20 bg-card px-3 py-2 text-left font-medium">Supervisor</th>
                    <th className="px-2 py-2 text-center font-medium">P</th>
                    <th className="px-2 py-2 text-center font-medium">A</th>
                    <th className="px-2 py-2 text-center font-medium">H</th>
                    {dayHeaders.map((d, i) => (
                      <th key={i} className="px-1 py-2 text-center font-medium">{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.supervisor_id} className="border-t" data-testid={`sup-cal-row-${r.supervisor_id}`}>
                      <td className="sticky left-0 z-10 bg-card px-3 py-1.5">
                        <button
                          type="button"
                          onClick={() => openDrill(r)}
                          className="text-left transition-colors hover:text-primary"
                          data-testid={`sup-cal-drill-${r.supervisor_id}`}
                        >
                          <div className="font-medium leading-tight">{r.supervisor_name}</div>
                          <div className="text-[11px] font-mono text-muted-foreground">{r.username}</div>
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-center font-semibold text-emerald-700">{r.present}</td>
                      <td className="px-2 py-1.5 text-center font-semibold text-rose-700">{r.absent}</td>
                      <td className="px-2 py-1.5 text-center font-semibold text-violet-700">{r.holidays}</td>
                      {r.cells.map((c) => (
                        <td key={c.date} className="px-1 py-1">
                          <SupervisorCellPopover
                            row={r}
                            cell={c}
                            onChoose={(code) => setOverride(r.supervisor_id, c.date, code)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500" /> P · Present</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-rose-200" /> A · Absent</span>
          <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-violet-500" /> H · Holiday (counted as Present)</span>
          <span className="mx-2 h-3 w-px bg-border" />
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> Manually overridden</span>
        </div>
      </div>

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {drill?.supervisor?.name || "Supervisor"} — selfies this month
            </DialogTitle>
            <div className="text-xs text-muted-foreground">
              {drill?.from && drill?.to ? <>{fmtDate(drill.from)} → {fmtDate(drill.to)}</> : null}
            </div>
          </DialogHeader>
          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto">
            {(!drill?.captures || drill.captures.length === 0) && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                No selfies in this range.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="sup-drill-grid">
              {(drill?.captures || []).map((c) => {
                const src = c.photo_b64
                  ? (c.photo_b64.startsWith("data:") ? c.photo_b64 : `data:image/jpeg;base64,${c.photo_b64}`)
                  : "";
                const hasPhoto = !!c.photo_b64;
                return (
                  <div
                    key={c.id}
                    className="overflow-hidden rounded-lg border"
                    data-testid={`sup-drill-card-${c.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => hasPhoto && setLightbox({
                        src,
                        caption: `${fmtDate(c.date)} · ${fmtTime(c.created_at)}${c.park_name ? ` · ${c.park_name}` : ""}`,
                      })}
                      disabled={!hasPhoto}
                      className={`group relative block aspect-[4/5] w-full bg-slate-900/95 ${hasPhoto ? "cursor-zoom-in" : "cursor-not-allowed"}`}
                      data-testid={`sup-drill-img-${c.id}`}
                      aria-label="Open photo"
                    >
                      {hasPhoto ? (
                        <img
                          src={src}
                          alt=""
                          className="absolute inset-0 h-full w-full object-contain transition-transform duration-200 group-hover:scale-[1.01]"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-white/70">No photo</div>
                      )}
                      <div className="absolute right-2 top-2">
                        <Badge variant="secondary" className="opacity-90">{c.window_label || "Open"}</Badge>
                      </div>
                    </button>
                    <div className="p-2 text-xs">
                      <div className="font-medium">{fmtDate(c.date)} · {fmtTime(c.created_at)}</div>
                      {c.park_name ? <div className="text-muted-foreground truncate">{c.park_name}</div> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setDrill(null)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {lightbox ? (
        <ImageLightbox
          src={lightbox.src}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </AppShell>
  );
}
